// PreviewOriginal.js (functional drop-in)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import helpers from 'dom-helpers';
import Loading from './Loading';

const sides = { top: 0, right: 1, bottom: 2, left: 3 };
const storeSize = {};

export default function PreviewOriginal({
                                            url,
                                            overlayID,
                                            originalWidth,
                                            originalHeight,
                                            indents = [],
                                            placement,
                                            className,
                                            children,
                                        }) {
    const overlay = useMemo(() => (overlayID ? document.getElementById(overlayID) : null), [overlayID]);
    const [size, setSize] = useState({ width: originalWidth, height: originalHeight });
    const [loaded, setLoaded] = useState(Boolean(originalWidth && originalHeight));
    const imgRef = useRef(null);

    const maxBox = () => {
        const doc = document.documentElement;
        const clientWidth = doc.clientWidth;
        const clientHeight = doc.clientHeight;
        const overlayBox = helpers.offset(overlay);
        const scrollTop = doc.scrollTop;

        let maxWidth = clientWidth - getIndent('left') - getIndent('right');
        let maxHeight = clientHeight - getIndent('top') - getIndent('bottom');

        if (placement === 'top') {
            maxHeight -= clientHeight - (overlayBox.top - scrollTop);
        } else if (placement === 'bottom') {
            maxHeight -= overlayBox.top - scrollTop + overlayBox.height;
        } else if (placement === 'left') {
            maxWidth -= clientWidth - overlayBox.left;
        } else if (placement === 'right') {
            maxWidth -= overlayBox.left + overlayBox.width;
        }
        const w = size.width || originalWidth || 0;
        const h = size.height || originalHeight || 0;

        return {
            maxWidth: maxWidth > w ? w : maxWidth,
            maxHeight: maxHeight > h ? h : maxHeight,
        };
    };

    const getIndent = (side) => indents[sides[side]] || 0;

    const calc = (ow, oh) => {
        const ratio = ow / oh;
        const m = maxBox();
        const hMode = ratio > 1;
        let width = hMode ? m.maxWidth : m.maxHeight * ratio;
        let height = hMode ? width / ratio : m.maxHeight;
        if (hMode && height > m.maxHeight) {
            height = m.maxHeight; width = height * ratio;
        } else if (!hMode && width > m.maxWidth) {
            width = m.maxWidth; height = width / ratio;
        }
        return { width, height };
    };

    useEffect(() => {
        if (originalWidth && originalHeight) {
            setSize(calc(originalWidth, originalHeight));
            setLoaded(true);
            return;
        }
        if (!url) return;

        const cached = storeSize[url];
        if (cached?.width && cached?.height) {
            setSize(calc(cached.width, cached.height));
            setLoaded(true);
            return;
        }

        const img = new Image();
        imgRef.current = img;
        img.onload = () => {
            storeSize[url] = { width: img.naturalWidth, height: img.naturalHeight };
            setSize(calc(img.naturalWidth, img.naturalHeight));
            setLoaded(true);
        };
        img.src = url;

        return () => { if (imgRef.current) imgRef.current.onload = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, overlayID, placement, originalWidth, originalHeight]);

    const cls = classNames('original-preview', className);

    return (
        <div className={cls} style={{ width: size.width, height: size.height }}>
            {!loaded ? <Loading center /> : children}
        </div>
    );
}

PreviewOriginal.propTypes = {
    url: PropTypes.string,
    overlayID: PropTypes.string,
    originalWidth: PropTypes.number,
    originalHeight: PropTypes.number,
    indents: PropTypes.array,
    placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    className: PropTypes.string,
    children: PropTypes.node,
};
